import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { IOption } from '@cmps/ng-select';
import { of } from 'rxjs';
import { catchError, debounceTime, filter, tap } from 'rxjs/operators';

import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { User } from '../../../user/user.model';
import { ScorecardGridOptions } from '../../core/scorecard-grid-options.model';
import {
    GridType,
    TemplateMode,
    Widget,
} from '../../core/template-definition.model';
import {
    bindWidthResize,
    Column,
    Context,
    DivRendererOptions,
    ExperimentalNgGridComponent,
    GridContent,
    SelectTemplateFn,
    SortOrder,
} from '../../experimental.grid';
import { TranslationKeyMap } from '../../scorecard.i18n';
import { CMPS_SCORECARD_WIDGET } from '../../scorecard.metadata';

import {
    addColumnsMeasure,
    addRowsMeasure,
    autoSizeGrid,
} from './auto-size-grid';
import {
    CellContext,
    DirectReportRankDisplay,
    RankColumnContext,
    RankColumnType,
} from './direct-report-rank-grid.model';
import { GridEpicBridge } from './grid.epic.bridge';
import { ScoredardBridgeService } from './sc-bridge.service';
import { ScorecardSelection } from './team-grid.model';
import { DirectReportRankGrid } from './team-grid.state';

@Component({
    selector: 'cmps-direct-report-rank-grid',
    templateUrl: './direct-report-rank-grid.component.html',
    host: {
        class: 'cmps-direct-report-rank-grid',
    },
})
@TakeUntilDestroy()
export class DirectReportrankGridComponent
    implements OnChanges, OnInit, OnDestroy
{
    @ViewChild(ExperimentalNgGridComponent)
    private readonly _grid: ExperimentalNgGridComponent;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _ColumnType = RankColumnType;
    /** @private */ _SortOrder = SortOrder;
    /** @private */ _scList: IOption[];

    @Input() mode: TemplateMode;
    @Input() model: DirectReportRankGrid;
    /** @private */ _prevModel: DirectReportRankGrid;
    @Output() sortChange: EventEmitter<string>;
    @Output() onReload: EventEmitter<void>;
    @Output() onDRSCSelect: EventEmitter<ScorecardSelection>;
    @Output() onGridSelect: EventEmitter<GridType>;
    private _blackoutBindWidthResize: boolean;

    /** @private */ _rendererOpts: DivRendererOptions;
    /** @private */ _selectTpl: SelectTemplateFn = (
        context: Context<RankColumnContext, RankColumnContext | CellContext>,
        contents: QueryList<GridContent>,
    ): GridContent => {
        if (context.t === 'grid') {
            return contents.find((e) => e.type === 'grid');
        } else if (context.t === 'row') {
            return contents.find((e) => e.type === 'row');
        } else if (context.t === 'cell') {
            return contents.find(
                (e) => e.type === 'cell' && e.id === context.ctx.templateId,
            );
        } else {
            return null;
        }
    };

    public headerColour: string;

    constructor(
        private readonly _changeDetectorRef: ChangeDetectorRef,
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _scBridge: ScoredardBridgeService,
        private readonly _epicBridge: GridEpicBridge,
        @Inject(CMPS_SCORECARD_WIDGET) /** @private */
        public _widget: Widget<ScorecardGridOptions>,
    ) {
        this.sortChange = new EventEmitter();
        this.onReload = new EventEmitter();
        this.onDRSCSelect = new EventEmitter();
        this.onGridSelect = new EventEmitter();
        this.headerColour = _widget.options.directReportGrids.find(
            (g) => g.gridType === 'rankView',
        ).headerColour;
    }

    ngOnInit(): void {
        this.model.sortDefs = [];
        this._rendererOpts = {
            columns: this.model ? this.model.columns : null,
            defaultRenderFn: null,
            height: 350,
            horizontalScrollPadding: 9999,
            verticalScrollPadding: 9999,
        };
        if (this.model.rows && this.model.rows.length > 0) {
            this._scList = this._generateSGList();
        }
        this.onGridSelect.emit(GridType.Rank_View);

        // TODO revise. a problem with the pinned scorecard header +
        //  window scroll height causing the window scrollbar to flicker +
        //  while the window is resizing can cause edge-case weirdness
        bindWidthResize(this._el.nativeElement.parentElement.parentElement)
            .pipe(
                filter(() => !this._blackoutBindWidthResize),
                tap(() => this._onWillInflate()),
                debounceTime(250),
                tap(() => this._onDidInflate()),
                catchError(() => of(null)),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */ _onSortClick(column: Column<RankColumnContext>): void {
        this.sortChange.emit(column.id);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._grid.renderer.layout();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('model')) {
            if (
                (!this._prevModel && this.model) ||
                (this._prevModel &&
                    this.model &&
                    this._prevModel.columns !== this.model.columns)
            ) {
                addColumnsMeasure(this.model.columns);

                this._rendererOpts = {
                    ...this._rendererOpts,
                    columns: this.model.columns,
                };
            }

            if (
                (!this._prevModel && this.model) ||
                (this._prevModel &&
                    this.model &&
                    this._prevModel.rows !== this.model.rows)
            ) {
                addRowsMeasure(this.model.rows);

                if (
                    this._prevModel &&
                    this._prevModel.columns === this.model.columns
                ) {
                    this._onWillInflate();
                    this._onDidInflate();
                }
            }

            if (
                ((!this._prevModel && this.model) ||
                    (this._prevModel &&
                        this.model &&
                        this._prevModel.sortDefs !== this.model.sortDefs)) &&
                this._grid &&
                this._grid.renderer
            ) {
                this._grid.renderer.contentColumns.columns.forEach((r) =>
                    r.cells.forEach((l) => l.detectChanges({ onlySelf: true })),
                );
            }

            this._prevModel = this.model;
        }
    }

    ngOnDestroy(): void {
        this.sortChange.complete();
        this.onReload.complete();
        this.onDRSCSelect.complete();
        this.onGridSelect.complete();
    }

    layout(): void {
        this._onWillInflate();
        this._onDidInflate();
    }

    /** @private */
    _onWillInflate(): void {
        // TODO revise. this is just a mitigation. the real problem is scorecardComponent.stateToProps()
        if (!this._scBridge.componentScorecard.bridgeIsExporting()) {
            this._el.nativeElement.style.visibility = 'hidden';
        }
        this._blackoutBindWidthResize = true;
    }

    /** @private */
    _onDidInflate(): void {
        this._changeDetectorRef.detectChanges();
        if (!this._grid || !this._grid.renderer) {
            return;
        }

        if (this.model.rows && this.model.rows.length > 0) {
            this._scList = this._generateSGList();
        }

        this._epicBridge.renderer_direct_reports = this._grid.renderer;

        const leftColumns = [
            RankColumnType.DIRECT_REPORTS,
            RankColumnType.SUPERGROUP_NAME,
            RankColumnType.PERIOD,
        ].map(String);
        const columns = this.model.columns;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const left =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            columns && columns.filter((e) => leftColumns.includes(e.id));
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const right =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            columns && columns.filter((e) => !leftColumns.includes(e.id));
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        autoSizeGrid(
            this._grid.renderer,
            left,
            right,
            this._el.nativeElement.parentElement.parentElement,
        ).then(() => {
            this._el.nativeElement.style.visibility = '';
            this._blackoutBindWidthResize = false;
        });
    }

    /** @private */ _generateSGList(): IOption[] {
        const listedSG = new Set();
        const SGFilterItems = new Array<IOption>();
        this.model.rows.forEach((r) => {
            r.cells.forEach((c) => {
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                listedSG.add(
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    `${c.ctx.data.subSuperGroupId}**${c.ctx.data.subSuperGroupName}`,
                );
            });
        });
        listedSG.forEach((sg: string) => {
            SGFilterItems.push({
                value: sg.split('**')[0],
                label: sg.split('**')[1],
            });
        });
        SGFilterItems.sort((a, b) => a.label.localeCompare(b.label));
        return SGFilterItems;
    }

    /** @private */ _onDRScorecardSelect(item: DirectReportRankDisplay): void {
        const user = new User();
        user.id = item.subUserId;
        user.superGroup = item.subSuperGroupName;
        const names = item.directReport.split(' ');
        user.firstName = names[0];
        user.lastName = names[1];
        const scSelection: ScorecardSelection = {
            user,
            scorecardId: item.subScorecardId,
        };
        this.onDRSCSelect.emit(scSelection);
    }
}
